/* eslint-disable no-magic-numbers */

import {
  DescriptionList,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useContractor } from '@omnipresentgroup/domains';
import type { Country, GetContractorResponse } from '@omnipresentgroup/types';
import { useMemo } from 'react';

type Renderer = (contractor: GetContractorResponse) => string | JSX.Element;

const CONTRACTOR_ENTRIES = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'jobRole',
    label: 'Role',
  },
  {
    key: 'residenceCountry',
    label: 'Country of residence',
  },
];

const fullNameRenderer = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`;

const countryNameRenderer = (country: Country) => country?.name ?? '';

const contractorRendererMapping: {
  [key: (typeof CONTRACTOR_ENTRIES)[number]['key']]: Renderer;
} = {
  name: (contractor: GetContractorResponse) =>
    fullNameRenderer(contractor.firstName, contractor.lastName),
  jobRole: (contractor: GetContractorResponse) =>
    contractor.contracts[0].jobRole,
  residenceCountry: (contractor: GetContractorResponse) =>
    countryNameRenderer(contractor.residenceCountry),
};

export const ContractorPersonalDetails = ({
  contractorId,
}: {
  contractorId: string;
}) => {
  const { data: contractor, isLoading } = useContractor({ id: contractorId });

  const contractorDetails = useMemo(
    () =>
      CONTRACTOR_ENTRIES.map(({ label, key }) => ({
        header: label,
        detail: isLoading ? (
          <Skeleton key={key} />
        ) : // eslint-disable-next-line unicorn/no-nested-ternary
        contractor &&
          typeof contractorRendererMapping[
            key as (typeof CONTRACTOR_ENTRIES)[number]['key']
          ] === 'function' ? (
          contractorRendererMapping?.[key](contractor)
        ) : (
          (contractor?.[key as keyof GetContractorResponse] as
            | string
            | JSX.Element)
        ),
      })),
    [contractor, isLoading],
  );

  return (
    <Stack data-testid="contractor-details-list">
      <Typography as="h3" hideParagraphSpacing size="20" weight="medium">
        Personal details
      </Typography>
      <DescriptionList
        fraction="1/2"
        id="contractor-details-list"
        list={contractorDetails}
      />
    </Stack>
  );
};
